/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {TextField, Autocomplete} from '@mui/material'

import {createFilterOptions} from '@mui/material/Autocomplete'

import * as Yup from 'yup'
import {withFormik, Form, Field} from 'formik'

import FormButton from '../FormButton'
import FormikTextField from './FormikTextField'
import FormikPhoneNumberField from './FormikPhoneNumberField'

import {Errors, CloseButton} from '../../../generics'


const filter = createFilterOptions()

const styles = {
  root: css`
    max-width: 360px;
    padding: 30px 40px;
    position: relative;

    h2 {
      font-weight: 500;
      margin-bottom: 20px;
    }
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
  buttonsContainer: css`
    display: flex;
  `
}


const GROUP_OPTIONS = ['General', 'Friends', 'Family', 'Work', 'VIP']


const ContactForm = ({
  close,
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue
}) => {
  return (
    <div css={styles.root}>
      <CloseButton onClick={close} />

      <h2>Add Contact</h2>

      <Errors errors={errors.form} />

      <Form css={styles.form}>
        <Field
          component={FormikTextField}
          name='name'
          label='Name'
          fullWidth
        />
        <Field
          component={FormikTextField}
          name='email'
          label='Email'
          type='email'
          fullWidth
        />
        <Field
          component={FormikPhoneNumberField}
          name='phone'
          label='Phone'
          fullWidth
        />
        <Autocomplete
          multiple
          freeSolo
          options={GROUP_OPTIONS}
          value={values.groups}
          onChange={(_e, value) => setFieldValue('groups', value)}
          filterOptions={(options, params) => {
            const filtered = filter(options, params)
            // if the user typed something that isn't already in the list, add it as a new option
            if (params.inputValue !== '' && !GROUP_OPTIONS.includes(params.inputValue)) {
              filtered.push(params.inputValue)
            }
            return filtered
          }}
          renderInput={(params) => {
            const showError = Boolean(touched.groups && errors.groups)
            return (
              <TextField 
                {...params}
                css={styles.dropdown}
                name='groups'
                label='Groups' 
                variant='filled'
                error={showError} 
                fullWidth
                helperText={showError 
                  ? errors.groups 
                  : 'Select a group from the dropdown or type your own and press Enter.'
                }
                sx={{
                  cursor: 'pointer',
                  '& .MuiFilledInput-root': {
                    cursor: 'pointer',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '& .MuiFilledInput-input': {
                    cursor: 'pointer',
                    padding: '8px 12px !important',
                  }
                }}
              />
            )
          }}
        />


        <div css={styles.buttonsContainer}>
          <FormButton type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </FormButton>
        </div>
      </Form>
    </div>
  )
}




const EnhancedContactForm = withFormik({
  mapPropsToValues: () => ({
    name: '',
    phone: '',
    email: '',
    groups: []
  }),

  validationSchema: Yup.object({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email'),
    groups: Yup.array().of(Yup.string()).min(1, 'Please select at least one group')
  }),

  handleSubmit: async (values, { props, setSubmitting, resetForm, setErrors }) => {
    try {
      // await the onSave promise; let it reject if the server returns 422
      await props.onSave(values)
      // on success, clear + close
      resetForm()
      props.close()

    } catch (err) {
      const msgs = err.response?.data?.error?.messages

      if (msgs) {
        setErrors({form: msgs})
      } else {
        setErrors({form: 'Failed to save contact.'})
      }
    } finally {
      setSubmitting(false)
    }
  },

  displayName: 'AddContactForm'
})(ContactForm)



export default EnhancedContactForm



